import { Link, useNavigate } from 'react-router-dom'
import type { ConsoleBusiness } from '../../lib/api'
import {
  FEED_STATE_LABEL,
  businessTone,
  dayNotSeen,
  money,
  spendRatio,
  stoppedFeeds,
  SPEND_AMBER_RATIO,
} from '../../lib/consoleStatus'
import { cn } from '../../lib/utils'
import { h2Class, kicker, when } from '../soc/SocShell'
import { SectionMessage } from './ConsoleShell'

// BRIEF-F1b, Businesses: one row per business from /hub/console/businesses.
// Red when a watched person's feed stopped or the readout day had someone not
// read or not seen; amber at 80 per cent of the day's cap; armed is a label.
// A row opens that business's readout on its own page.

const toneRow = {
  red: 'border-l-[6px] border-l-danger bg-danger-light',
  amber: 'border-l-[6px] border-l-warning bg-warning-light',
  plain: 'border-l-[6px] border-l-transparent bg-white',
} as const

function Reasons({ b }: { b: ConsoleBusiness }) {
  const stopped = stoppedFeeds(b)
  const lines: { text: string; red: boolean }[] = []
  for (const f of stopped) {
    lines.push({
      text: `Feed ${FEED_STATE_LABEL[f.state] ?? f.state}: ${f.upn}${
        f.hours_since_sync !== null ? ` (${Math.round(f.hours_since_sync)} h since last read)` : ''
      }`,
      red: true,
    })
  }
  const h = b.readout_headline
  if (dayNotSeen(b)) {
    lines.push({
      text:
        h.people === 0
          ? `Shift is on but nobody was there to read on ${b.readout_day}`
          : `${h.not_read_or_not_seen} of ${h.people} not read or not seen on ${b.readout_day}`,
      red: true,
    })
  }
  const others = b.feeds.filter((f) => !(f.watched && stopped.includes(f)) && f.state !== 'fresh')
  for (const f of others) {
    lines.push({
      text: `Feed ${FEED_STATE_LABEL[f.state] ?? f.state}: ${f.upn}${f.watched ? '' : ' (not watched)'}`,
      red: false,
    })
  }
  if (lines.length === 0) return null
  return (
    <ul className="m-0 mt-1 list-none p-0 text-[13px]">
      {lines.map((l) => (
        <li key={l.text} className={l.red ? 'font-bold text-danger' : 'text-[#6b5a3e]'}>
          {l.text}
        </li>
      ))}
    </ul>
  )
}

function BusinessRow({ b }: { b: ConsoleBusiness }) {
  const navigate = useNavigate()
  const tone = businessTone(b)
  const ratio = spendRatio(b)
  const h = b.readout_headline
  const href = `/console/business/${encodeURIComponent(b.tenant_id)}`
  return (
    <tr
      data-testid={`business-row-${b.tenant_id}`}
      data-tone={tone}
      onClick={() => navigate(href)}
      className={cn('cursor-pointer border-b border-[#e6dfd2] align-top hover:brightness-[.98]', toneRow[tone])}
    >
      <td className="px-4 py-3">
        <Link
          to={href}
          onClick={(e) => e.stopPropagation()}
          className="font-serif text-[18px] text-navy no-underline hover:underline"
        >
          {b.name}
        </Link>
        <div className="text-[13px] text-[#6b5a3e]">
          {b.cloud ?? 'no cloud linked'}
          {!b.watched && ' · shift off'}
          {b.close_watch && ' · close watch'}
        </div>
        <Reasons b={b} />
      </td>
      <td className="px-4 py-3 text-[14px]">
        {b.people_watched} watched
        {b.people_not_watched > 0 && (
          <div className="text-[13px] text-[#6b5a3e]">{b.people_not_watched} not watched</div>
        )}
      </td>
      <td className="px-4 py-3 text-[14px]">
        <div>
          {h.read} of {h.people} read
        </div>
        <div className="text-[13px] text-[#6b5a3e]">
          {h.questions} question{h.questions === 1 ? '' : 's'} · {h.escalations} escalated
        </div>
        <div className="text-[13px] text-[#6b5a3e]">{b.readout_day}</div>
      </td>
      <td className="px-4 py-3 text-[14px]">
        <span className={cn(ratio >= SPEND_AMBER_RATIO && 'font-bold text-[#92400e]')}>
          {money(b.spent_today_usd)} of {money(b.daily_cap_usd)}
        </span>
        <div className="text-[13px] text-[#6b5a3e]">
          {Math.round(ratio * 100)}% of {b.cap_is_own ? 'its own cap' : 'the standard cap'}
          {ratio >= 1 && ' · at cap'}
        </div>
      </td>
      <td className="px-4 py-3 text-[14px]">
        <span
          data-testid="armed-label"
          className="inline-block rounded border border-[#e6dfd2] bg-white px-2 py-0.5 text-[13px]"
        >
          {b.response_armed ? 'Armed' : 'Not armed'}
        </span>
      </td>
      <td className="px-4 py-3 text-[13px] text-[#6b5a3e]">
        {when(b.newest_event_at) ?? 'nothing stored'}
      </td>
    </tr>
  )
}

export function ConsoleBusinesses({
  businesses,
  error,
}: {
  businesses: ConsoleBusiness[] | null
  error: string | null
}) {
  return (
    <section id="businesses" aria-label="Businesses" className="flex flex-col gap-3.5">
      <div>
        <div className={kicker}>Every business, the last readout day</div>
        <h2 className={h2Class}>Businesses</h2>
      </div>
      {error !== null ? (
        <SectionMessage tone="error">Could not load: {error}</SectionMessage>
      ) : businesses === null ? (
        <SectionMessage>Loading the businesses…</SectionMessage>
      ) : businesses.length === 0 ? (
        <SectionMessage>No business is enrolled yet.</SectionMessage>
      ) : (
        <div className="overflow-x-auto rounded-[10px] border border-[#e6dfd2] bg-white">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-[#e6dfd2] text-[12px] uppercase tracking-[.1em] text-[#6b5a3e]">
                <th className="px-4 py-2 font-normal">Business</th>
                <th className="px-4 py-2 font-normal">People</th>
                <th className="px-4 py-2 font-normal">Readout</th>
                <th className="px-4 py-2 font-normal">Spend today</th>
                <th className="px-4 py-2 font-normal">Response</th>
                <th className="px-4 py-2 font-normal">Newest event</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <BusinessRow key={b.tenant_id} b={b} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
